const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:3333/api/v1";
const TOKEN_KEY = "nf_app_token";

function authHeaders(extra?: Record<string, string>) {
  const token = localStorage.getItem(TOKEN_KEY) ?? "";
  return { Authorization: `Bearer ${token}`, ...extra };
}

/** Usuário desativado pelo RH: o backend recusa token e refresh com este código. */
let acessoDesativado = false;

async function tryRefreshToken(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      if (body?.error?.code === "USUARIO_INATIVO") acessoDesativado = true;
      return false;
    }
    const json = await res.json();
    const token = json.data?.token as string | undefined;
    if (!token) return false;
    localStorage.setItem(TOKEN_KEY, token);
    return true;
  } catch {
    return false;
  }
}

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

function extractErrorMessage(body: unknown, status: number): string {
  const err = (body as { error?: { message?: string } | string })?.error;
  if (typeof err === "string") return err;
  return err?.message ?? `Erro ${status}`;
}

/** Corpo JSON da resposta; 204 ou corpo vazio viram undefined em vez de quebrar no parse. */
async function lerJson(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;
  const texto = await res.text();
  return texto ? JSON.parse(texto) : undefined;
}

const extrairData = <T>(json: unknown): T =>
  (json && typeof json === "object" && "data" in json ? (json as { data: unknown }).data : json) as T;

function encerrarSessao(): never {
  localStorage.removeItem(TOKEN_KEY);
  window.location.href = acessoDesativado ? "/login?motivo=inativo" : "/login";
  throw new Error(acessoDesativado ? "Acesso desativado" : "Sessão expirada");
}

async function handleResponse<T>(res: Response, retry: () => Promise<Response>): Promise<T> {
  if (res.status === 401) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      const retried = await retry();
      if (retried.ok) return extrairData<T>(await lerJson(retried));
    }
    encerrarSessao();
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(extractErrorMessage(body, res.status), res.status, body);
  }
  return extrairData<T>(await lerJson(res));
}

export const api = {
  get: <T>(path: string) => {
    const doFetch = () =>
      fetch(`${API_BASE}${path}`, { headers: authHeaders(), credentials: "include" });
    return doFetch().then((r) => handleResponse<T>(r, doFetch));
  },

  post: <T>(path: string, body: unknown) => {
    const doFetch = () =>
      fetch(`${API_BASE}${path}`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(body),
        credentials: "include",
      });
    return doFetch().then((r) => handleResponse<T>(r, doFetch));
  },

  postForm: <T>(path: string, formData: FormData) => {
    const doFetch = () =>
      fetch(`${API_BASE}${path}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) ?? ""}` },
        body: formData,
        credentials: "include",
      });
    return doFetch().then((r) => handleResponse<T>(r, doFetch));
  },

  put: <T>(path: string, body?: unknown) => {
    const doFetch = () =>
      fetch(`${API_BASE}${path}`, {
        method: "PUT",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: body ? JSON.stringify(body) : undefined,
        credentials: "include",
      });
    return doFetch().then((r) => handleResponse<T>(r, doFetch));
  },

  patch: <T>(path: string, body?: unknown) => {
    const doFetch = () =>
      fetch(`${API_BASE}${path}`, {
        method: "PATCH",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: body ? JSON.stringify(body) : undefined,
        credentials: "include",
      });
    return doFetch().then((r) => handleResponse<T>(r, doFetch));
  },

  getFile: async (path: string): Promise<{ blob: Blob; filename: string }> => {
    const token = localStorage.getItem(TOKEN_KEY) ?? "";
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ApiError(extractErrorMessage(body, res.status), res.status, body);
    }
    const disposition = res.headers.get("content-disposition") ?? "";
    const starMatch = disposition.match(/filename\*=UTF-8''([^;\n\s]+)/i);
    const plainMatch = disposition.match(/filename="([^"]+)"/);
    const filename = starMatch
      ? decodeURIComponent(starMatch[1])
      : plainMatch
        ? plainMatch[1]
        : "download";
    const blob = await res.blob();
    return { blob, filename };
  },

  delete: (path: string, body?: unknown) => {
    const headers = body ? authHeaders({ "Content-Type": "application/json" }) : authHeaders();
    const doFetch = () =>
      fetch(`${API_BASE}${path}`, {
        method: "DELETE",
        headers,
        body: body ? JSON.stringify(body) : undefined,
        credentials: "include",
      });
    return doFetch().then(async (r) => {
      if (r.status === 401) {
        const refreshed = await tryRefreshToken();
        if (refreshed) {
          const retried = await doFetch();
          if (retried.ok) return;
        }
        encerrarSessao();
      }
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        throw new ApiError(extractErrorMessage(errBody, r.status), r.status, errBody);
      }
    });
  },
};
